import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { invoiceAPI, customerAPI, consigneeAPI } from '../services/api';
import { downloadInvoicePDF, printInvoicePDF } from '../utils/pdfGenerator';
import './InvoiceForm.css';

function InvoiceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [customers, setCustomers] = useState([]);
  const [consignees, setConsignees] = useState([]);
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    customer: '',
    consignee: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    loads: [],
    fuelSurchargeRate: 0,
    notes: '',
    status: 'draft'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCustomers();
    fetchConsignees();
    if (isEditMode) {
      fetchInvoice();
    } else {
      fetchNextInvoiceNumber();
    }
  }, [id]);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getAll();
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchConsignees = async () => {
    try {
      const response = await consigneeAPI.getAll();
      setConsignees(response.data);
    } catch (error) {
      console.error('Error fetching consignees:', error);
    }
  };

  const fetchNextInvoiceNumber = async () => {
    try {
      const response = await invoiceAPI.getNextNumber();
      setFormData(prev => ({
        ...prev,
        invoiceNumber: response.data.invoiceNumber
      }));
    } catch (error) {
      console.error('Error fetching next invoice number:', error);
    }
  };

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await invoiceAPI.getById(id);
      const invoice = response.data;

      setFormData({
        invoiceNumber: invoice.invoiceNumber,
        customer: invoice.customer._id,
        consignee: invoice.consignee?._id || '',
        invoiceDate: new Date(invoice.invoiceDate).toISOString().split('T')[0],
        loads: invoice.loads.map(load => ({
          ...load,
          loadType: load.loadType || '',
          quantity: load.quantity || '',
          numberOfLoads: load.numberOfLoads || '',
          ratePerUnit: load.ratePerUnit || load.rate || '',
          calculatedRate: load.rate || 0,
          pickupDate: load.pickupDate ? new Date(load.pickupDate).toISOString().split('T')[0] : '',
          isConfirmed: true
        })),
        fuelSurchargeRate: invoice.fuelSurchargeRate || invoice.taxRate || 0,
        notes: invoice.notes || '',
        status: invoice.status
      });
    } catch (error) {
      console.error('Error fetching invoice:', error);
      alert('Failed to load invoice');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const addLoad = () => {
    setFormData({
      ...formData,
      loads: [
        ...formData.loads,
        {
          loadType: '',
          description: '',
          quantity: '',
          numberOfLoads: '',
          weight: '',
          weightUnit: 'lbs',
          pickupDate: '',
          bolNumber: '',
          poNumber: '',
          ratePerUnit: '',
          calculatedRate: 0,
          isConfirmed: false
        }
      ]
    });
  };

  const removeLoad = (index) => {
    setFormData({
      ...formData,
      loads: formData.loads.filter((_, i) => i !== index)
    });
  };

  const confirmLoad = (index) => {
    const newLoads = [...formData.loads];
    newLoads[index].isConfirmed = true;
    setFormData({
      ...formData,
      loads: newLoads
    });
  };

  const editLoad = (index) => {
    const newLoads = [...formData.loads];
    newLoads[index].isConfirmed = false;
    setFormData({
      ...formData,
      loads: newLoads
    });
  };

  const handleLoadChange = (index, field, value) => {
    const newLoads = [...formData.loads];
    newLoads[index][field] = value;

    // Handle load type changes
    if (field === 'loadType') {
      if (value === 'Bulk') {
        newLoads[index].weightUnit = 'tons';
        newLoads[index].description = 'Bulk';
      } else if (value === 'Pallets') {
        newLoads[index].description = 'Pallets';
        newLoads[index].weightUnit = 'lbs';
      } else if (value === 'Jumbo Bags') {
        newLoads[index].description = 'Jumbo Bags';
        newLoads[index].weightUnit = 'lbs';
      }
    }

    // Calculate rate based on load type
    const load = newLoads[index];
    if (load.loadType === 'Pallets') {
      // Pallets: flat rate
      const ratePerUnit = parseFloat(load.ratePerUnit) || 0;
      load.calculatedRate = ratePerUnit;
    } else if (load.loadType === 'Bulk') {
      // Bulk: tonnage × rate per ton
      const weight = parseFloat(load.weight) || 0;
      const ratePerUnit = parseFloat(load.ratePerUnit) || 0;
      load.calculatedRate = weight * ratePerUnit;
    } else if (load.loadType === 'Jumbo Bags') {
      // Jumbo Bags: numberOfLoads × flat rate per bag
      const numberOfLoads = parseFloat(load.numberOfLoads) || 0;
      const ratePerUnit = parseFloat(load.ratePerUnit) || 0;
      load.calculatedRate = numberOfLoads * ratePerUnit;
    }

    setFormData({
      ...formData,
      loads: newLoads
    });
  };

  const calculateSubtotal = () => {
    return formData.loads.reduce((sum, load) => {
      return sum + (parseFloat(load.calculatedRate) || 0);
    }, 0);
  };

  const calculateFuelSurcharge = () => {
    const subtotal = calculateSubtotal();
    return subtotal * (parseFloat(formData.fuelSurchargeRate) || 0) / 100;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateFuelSurcharge();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.invoiceNumber.trim()) {
      newErrors.invoiceNumber = 'Invoice number is required';
    }
    if (!formData.customer) {
      newErrors.customer = 'Customer is required';
    }
    if (!formData.invoiceDate) {
      newErrors.invoiceDate = 'Invoice date is required';
    }
    if (formData.loads.length === 0) {
      newErrors.loads = 'At least one load is required';
    }

    formData.loads.forEach((load, index) => {
      if (!load.loadType) {
        newErrors[`load_${index}_loadType`] = 'Load type is required';
      }
      if (load.loadType === 'Bulk' && (!load.weight || parseFloat(load.weight) <= 0)) {
        newErrors[`load_${index}_weight`] = 'Tonnage is required for bulk loads';
      }
      if (load.loadType === 'Jumbo Bags' && (!load.numberOfLoads || parseFloat(load.numberOfLoads) <= 0)) {
        newErrors[`load_${index}_numberOfLoads`] = 'Number of loads is required for Jumbo Bags';
      }
      if (!load.ratePerUnit || parseFloat(load.ratePerUnit) <= 0) {
        newErrors[`load_${index}_ratePerUnit`] = 'Valid rate is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert('Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);

      // Convert loads dates and numbers to proper types
      const invoiceData = {
        ...formData,
        loads: formData.loads.map(load => ({
          ...load,
          quantity: load.quantity ? parseFloat(load.quantity) : undefined,
          numberOfLoads: load.numberOfLoads ? parseFloat(load.numberOfLoads) : undefined,
          weight: load.weight ? parseFloat(load.weight) : undefined,
          ratePerUnit: parseFloat(load.ratePerUnit),
          rate: parseFloat(load.calculatedRate),
          pickupDate: load.pickupDate || undefined,
          deliveryDate: load.deliveryDate || undefined
        })),
        fuelSurchargeRate: parseFloat(formData.fuelSurchargeRate) || 0
      };

      if (isEditMode) {
        await invoiceAPI.update(id, invoiceData);
        alert('Invoice updated successfully');
      } else {
        await invoiceAPI.create(invoiceData);
        alert('Invoice created successfully');
      }
      navigate('/invoices');
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert(error.response?.data?.message || 'Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (isEditMode) {
      try {
        const response = await invoiceAPI.getById(id);
        await downloadInvoicePDF(response.data);
        // Log the download action
        try {
          await invoiceAPI.logDownload(id);
        } catch (logError) {
          console.error('Error logging download:', logError);
        }
      } catch (error) {
        console.error('Error downloading PDF:', error);
        alert('Failed to download PDF');
      }
    }
  };

  const handlePrintPDF = async () => {
    if (isEditMode) {
      try {
        const response = await invoiceAPI.getById(id);
        await printInvoicePDF(response.data);
        // Log the print action
        try {
          await invoiceAPI.logPrint(id);
        } catch (logError) {
          console.error('Error logging print:', logError);
        }
      } catch (error) {
        console.error('Error printing PDF:', error);
        alert('Failed to print PDF');
      }
    }
  };

  if (loading && isEditMode) {
    return <div className="loading">Loading invoice...</div>;
  }

  const subtotal = calculateSubtotal();
  const fuelSurcharge = calculateFuelSurcharge();
  const total = calculateTotal();

  return (
    <div className="invoice-form">
      <div className="page-header">
        <h2>{isEditMode ? 'Edit Invoice' : 'Create New Invoice'}</h2>
        <div className="header-actions">
          {isEditMode && (
            <>
              <button onClick={handleDownloadPDF} className="btn btn-secondary">
                📥 Download PDF
              </button>
              <button onClick={handlePrintPDF} className="btn btn-secondary">
                🖨️ Print
              </button>
            </>
          )}
          <Link to="/invoices" className="btn btn-secondary">
            Back to List
          </Link>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="form">
        <div className="form-section">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="invoiceNumber">Invoice Number *</label>
              <input
                type="text"
                id="invoiceNumber"
                name="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={handleChange}
                className={errors.invoiceNumber ? 'error' : ''}
                required
                readOnly
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
              {errors.invoiceNumber && (
                <span className="error-message">{errors.invoiceNumber}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customer">Customer *</label>
              <select
                id="customer"
                name="customer"
                value={formData.customer}
                onChange={handleChange}
                className={errors.customer ? 'error' : ''}
                required
              >
                <option value="">Select a customer</option>
                {customers.map((customer) => (
                  <option key={customer._id} value={customer._id}>
                    {customer.company ? `${customer.company} - ${customer.name}` : customer.name}
                  </option>
                ))}
              </select>
              {errors.customer && <span className="error-message">{errors.customer}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="consignee">Shipped To (Consignee)</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <select
                  id="consignee"
                  name="consignee"
                  value={formData.consignee}
                  onChange={handleChange}
                  style={{ flex: 1 }}
                >
                  <option value="">Select a consignee (optional)</option>
                  {consignees.map((consignee) => (
                    <option key={consignee._id} value={consignee._id}>
                      {consignee.company ? `${consignee.company} - ${consignee.name}` : consignee.name}
                    </option>
                  ))}
                </select>
                <Link to="/consignees/new" className="btn btn-small btn-secondary" target="_blank">
                  + Add New
                </Link>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="invoiceDate">Invoice Date *</label>
              <input
                type="date"
                id="invoiceDate"
                name="invoiceDate"
                value={formData.invoiceDate}
                onChange={handleChange}
                className={errors.invoiceDate ? 'error' : ''}
                required
              />
              {errors.invoiceDate && (
                <span className="error-message">{errors.invoiceDate}</span>
              )}
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header-with-action">
            <h3>Loads</h3>
            <button type="button" onClick={addLoad} className="btn btn-primary btn-small">
              + Add Load
            </button>
          </div>

          {errors.loads && <span className="error-message">{errors.loads}</span>}

          {formData.loads.length === 0 ? (
            <p className="empty-message">No loads added yet. Click "Add Load" to begin.</p>
          ) : (
            <div className="loads-list">
              {formData.loads.map((load, index) => (
                <div key={index} className={`load-item ${load.isConfirmed ? 'load-confirmed' : ''}`}>
                  {load.isConfirmed ? (
                    // Confirmed Load - Invoice Preview Format
                    <div className="load-preview">
                      <div className="load-preview-header">
                        <span className="load-number">Load {index + 1}</span>
                        <div className="load-preview-actions">
                          <button
                            type="button"
                            onClick={() => editLoad(index)}
                            className="btn btn-small btn-secondary"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => removeLoad(index)}
                            className="btn btn-small btn-danger"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div className="load-preview-table">
                        <div className="preview-row preview-header">
                          <div className="preview-col col-date">Date</div>
                          <div className="preview-col col-type">Load Type</div>
                          <div className="preview-col col-units">Units</div>
                          <div className="preview-col col-unit-price">Unit Price</div>
                          <div className="preview-col col-bol">BOL #</div>
                          <div className="preview-col col-po">PO #</div>
                          <div className="preview-col col-amount">Amount</div>
                        </div>
                        <div className="preview-row preview-data">
                          <div className="preview-col col-date">
                            {load.pickupDate ? new Date(load.pickupDate).toLocaleDateString() : '-'}
                          </div>
                          <div className="preview-col col-type">{load.loadType}</div>
                          <div className="preview-col col-units">
                            {load.loadType === 'Pallets' && (load.quantity ? `${load.quantity} pallets` : '-')}
                            {load.loadType === 'Bulk' && `${(parseFloat(load.weight) || 0).toFixed(2)} tons`}
                            {load.loadType === 'Jumbo Bags' && (load.numberOfLoads ? `${load.numberOfLoads} loads` : '-')}
                          </div>
                          <div className="preview-col col-unit-price">
                            {load.loadType === 'Pallets' && 'Flat Rate'}
                            {load.loadType === 'Bulk' && `$${(parseFloat(load.ratePerUnit) || 0).toFixed(2)}`}
                            {load.loadType === 'Jumbo Bags' && `$${(parseFloat(load.ratePerUnit) || 0).toFixed(2)}`}
                          </div>
                          <div className="preview-col col-bol">{load.bolNumber || '-'}</div>
                          <div className="preview-col col-po">{load.poNumber || '-'}</div>
                          <div className="preview-col col-amount">
                            ${(load.calculatedRate || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Edit Mode - Full Form
                    <>
                      <div className="load-header">
                        <h4>Load {index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => removeLoad(index)}
                          className="btn btn-danger btn-small"
                        >
                          Remove
                        </button>
                      </div>

                  <div className="form-group">
                    <label htmlFor={`loadType-${index}`}>Load Type *</label>
                    <select
                      id={`loadType-${index}`}
                      value={load.loadType}
                      onChange={(e) => handleLoadChange(index, 'loadType', e.target.value)}
                      className={errors[`load_${index}_loadType`] ? 'error' : ''}
                    >
                      <option value="">Select Load Type</option>
                      <option value="Jumbo Bags">Jumbo Bags</option>
                      <option value="Bulk">Bulk</option>
                      <option value="Pallets">Pallets</option>
                    </select>
                    {errors[`load_${index}_loadType`] && (
                      <span className="error-message">{errors[`load_${index}_loadType`]}</span>
                    )}
                  </div>

                  {load.loadType === 'Pallets' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`quantity-${index}`}>Number of Pallets</label>
                        <input
                          type="number"
                          id={`quantity-${index}`}
                          value={load.quantity}
                          onChange={(e) => handleLoadChange(index, 'quantity', e.target.value)}
                          min="0"
                          step="1"
                          placeholder="0"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`ratePerUnit-${index}`}>Flat Rate *</label>
                        <input
                          type="number"
                          id={`ratePerUnit-${index}`}
                          value={load.ratePerUnit}
                          onChange={(e) => handleLoadChange(index, 'ratePerUnit', e.target.value)}
                          min="0"
                          step="0.01"
                          className={errors[`load_${index}_ratePerUnit`] ? 'error' : ''}
                          placeholder="0.00"
                        />
                        {errors[`load_${index}_ratePerUnit`] && (
                          <span className="error-message">{errors[`load_${index}_ratePerUnit`]}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Total Amount</label>
                        <input
                          type="text"
                          value={`$${(load.calculatedRate || 0).toFixed(2)}`}
                          disabled
                          className="calculated-field"
                        />
                      </div>
                    </div>
                  )}

                  {load.loadType === 'Bulk' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`weight-${index}`}>Tonnage *</label>
                        <input
                          type="number"
                          id={`weight-${index}`}
                          value={load.weight}
                          onChange={(e) => handleLoadChange(index, 'weight', e.target.value)}
                          min="0"
                          step="0.01"
                          className={errors[`load_${index}_weight`] ? 'error' : ''}
                          placeholder="0.00"
                        />
                        <small>Automatically rounded to 2 decimal places (tons)</small>
                        {errors[`load_${index}_weight`] && (
                          <span className="error-message">{errors[`load_${index}_weight`]}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor={`ratePerUnit-${index}`}>Rate Per Ton *</label>
                        <input
                          type="number"
                          id={`ratePerUnit-${index}`}
                          value={load.ratePerUnit}
                          onChange={(e) => handleLoadChange(index, 'ratePerUnit', e.target.value)}
                          min="0"
                          step="0.01"
                          className={errors[`load_${index}_ratePerUnit`] ? 'error' : ''}
                          placeholder="0.00"
                        />
                        {errors[`load_${index}_ratePerUnit`] && (
                          <span className="error-message">{errors[`load_${index}_ratePerUnit`]}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Total Amount</label>
                        <input
                          type="text"
                          value={`$${(load.calculatedRate || 0).toFixed(2)}`}
                          disabled
                          className="calculated-field"
                        />
                      </div>
                    </div>
                  )}

                  {load.loadType === 'Jumbo Bags' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`numberOfLoads-${index}`}>Number of Loads *</label>
                        <input
                          type="number"
                          id={`numberOfLoads-${index}`}
                          value={load.numberOfLoads}
                          onChange={(e) => handleLoadChange(index, 'numberOfLoads', e.target.value)}
                          min="0"
                          step="1"
                          className={errors[`load_${index}_numberOfLoads`] ? 'error' : ''}
                          placeholder="0"
                        />
                        {errors[`load_${index}_numberOfLoads`] && (
                          <span className="error-message">{errors[`load_${index}_numberOfLoads`]}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor={`ratePerUnit-${index}`}>Flat Rate Per Jumbo Bag *</label>
                        <input
                          type="number"
                          id={`ratePerUnit-${index}`}
                          value={load.ratePerUnit}
                          onChange={(e) => handleLoadChange(index, 'ratePerUnit', e.target.value)}
                          min="0"
                          step="0.01"
                          className={errors[`load_${index}_ratePerUnit`] ? 'error' : ''}
                          placeholder="0.00"
                        />
                        {errors[`load_${index}_ratePerUnit`] && (
                          <span className="error-message">{errors[`load_${index}_ratePerUnit`]}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Total Amount</label>
                        <input
                          type="text"
                          value={`$${(load.calculatedRate || 0).toFixed(2)}`}
                          disabled
                          className="calculated-field"
                        />
                      </div>
                    </div>
                  )}

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor={`pickupDate-${index}`}>Pickup Date</label>
                          <input
                            type="date"
                            id={`pickupDate-${index}`}
                            value={load.pickupDate}
                            onChange={(e) => handleLoadChange(index, 'pickupDate', e.target.value)}
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor={`bolNumber-${index}`}>BOL Number</label>
                          <input
                            type="text"
                            id={`bolNumber-${index}`}
                            value={load.bolNumber}
                            onChange={(e) => handleLoadChange(index, 'bolNumber', e.target.value)}
                            placeholder="Bill of Lading #"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor={`poNumber-${index}`}>PO Number</label>
                          <input
                            type="text"
                            id={`poNumber-${index}`}
                            value={load.poNumber}
                            onChange={(e) => handleLoadChange(index, 'poNumber', e.target.value)}
                            placeholder="Purchase Order #"
                          />
                        </div>
                      </div>

                      <div className="form-actions load-actions">
                        <button
                          type="button"
                          onClick={() => confirmLoad(index)}
                          className="btn btn-primary"
                          disabled={!load.loadType || !load.ratePerUnit ||
                            (load.loadType === 'Bulk' && !load.weight) ||
                            (load.loadType === 'Jumbo Bags' && !load.numberOfLoads)}
                        >
                          Confirm Load
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-section">
          <h3>Additional Details</h3>

          <div className="form-group">
            <label htmlFor="fuelSurchargeRate">Fuel Surcharge (%)</label>
            <input
              type="number"
              id="fuelSurchargeRate"
              name="fuelSurchargeRate"
              value={formData.fuelSurchargeRate}
              onChange={handleChange}
              min="0"
              max="100"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder="Any additional notes or payment terms..."
            />
          </div>
        </div>

        <div className="invoice-totals">
          <div className="totals-row">
            <span>Subtotal:</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {fuelSurcharge > 0 && (
            <div className="totals-row">
              <span>Fuel Surcharge ({formData.fuelSurchargeRate}%):</span>
              <span>${fuelSurcharge.toFixed(2)}</span>
            </div>
          )}
          <div className="totals-row total">
            <span>Total:</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving...' : isEditMode ? 'Update Invoice' : 'Create Invoice'}
          </button>
          <Link to="/invoices" className="btn btn-secondary">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

export default InvoiceForm;
